import { QuoteResultDto } from 'apiConnector/quote/dto/quoteResultDto';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import { quoteOrTabRemoved as buildQuoteOrTabRemoved } from 'epic/workspace/common/tabs/tabsEpics/quoteOrTabRemoved';
import { StaticFormResult } from 'models/forms/StaticFormResult';
import { ofType, StateObservable } from 'redux-observable';
import {
  catchError,
  delay,
  filter,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  race,
  SchedulerLike,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { Action } from 'state/actions';
import { commonQuoteAction } from 'state/forms/actions/quote';
import { EpicWithThunk, Thunk } from 'state/state.common';
import { AppState } from 'state/state.model';
import { QuoteResponse, smartQuoteActions } from 'state/workspaces/smart-quote/actions';
import { handleQuotePriceResponseThunk, handleQuoteResponseThunk } from 'state/workspaces/smart-quote/thunks';
import { IHttpClient } from 'utils/http.old/httpClient.model';
import { fatEventApiConnector } from 'utils/streaming/api';
import { staticQuoteResult } from 'utils/streaming/messages';
import { Streaming } from 'utils/streaming/model';

export function parsingResultStreamingEpic(): EpicWithThunk {
  return (action$, state$, { streaming }) =>
    action$.pipe(
      ofType('PARSE_EMAIL_SUCCESS'),
      switchMap(({ tabId, promptId }) => {
        const tabRemovedOrReset$ = tabRemovedOrReset(action$, tabId, state$);
        return streaming.smartQuoteMessage$.pipe(
          ofType('NextSmartQuoteParsingResult'),
          filter(res => res.promptId === promptId),
          map(nextSmartQuoteParsingResult => {
            return smartQuoteActions.parsingResult(tabId, nextSmartQuoteParsingResult);
          }),
          takeUntil(tabRemovedOrReset$),
        );
      }),
    );
}
export function parsingStartStreamingEpic(): EpicWithThunk {
  return (action$, state$, { streaming }) =>
    action$.pipe(
      ofType('PARSE_EMAIL_SUCCESS'),
      switchMap(({ tabId, promptId }) => {
        const tabRemovedOrReset$ = tabRemovedOrReset(action$, tabId, state$);
        return streaming.smartQuoteMessage$.pipe(
          ofType('NextSmartQuoteParsingStart'),
          filter(res => res.promptId === promptId),
          map(startEvent => smartQuoteActions.parsingStart(tabId, startEvent)),
          takeUntil(tabRemovedOrReset$),
        );
      }),
    );
}

export function parsingErrorStreamingEpic(): EpicWithThunk {
  return (action$, state$, { streaming }) =>
    action$.pipe(
      ofType('PARSE_EMAIL_SUCCESS'),
      switchMap(({ tabId, promptId }) => {
        const tabRemovedOrReset$ = tabRemovedOrReset(action$, tabId, state$);
        return streaming.smartQuoteMessage$.pipe(
          ofType('SmartQuoteParsingErrorEvent'),
          filter(res => res.promptId === promptId),
          map(nextSmartQuoteParsingResult => {
            return smartQuoteActions.parsingError(tabId, nextSmartQuoteParsingResult);
          }),
          takeUntil(tabRemovedOrReset$),
        );
      }),
    );
}

export function resultsTotalCountStreamingEpic(): EpicWithThunk {
  return (action$, state$, { streaming }) =>
    action$.pipe(
      ofType('PARSE_EMAIL_SUCCESS'),
      switchMap(({ tabId, promptId }) => {
        const tabRemovedOrReset$ = tabRemovedOrReset(action$, tabId, state$);

        return streaming.smartQuoteMessage$.pipe(
          ofType('SmartQuoteTotalCount'),
          filter(res => res.promptId === promptId),
          map(smartQuoteTotalCount => {
            return smartQuoteActions.addResultsTotalCount(tabId, smartQuoteTotalCount);
          }),
          takeUntil(tabRemovedOrReset$),
        );
      }),
    );
}

export function quoteResponseStreamingEpic(): EpicWithThunk {
  return (action$, state$, { streaming }) =>
    action$.pipe(
      ofType('PARSING_RESULT'),
      mergeMap(({ tabId, id, promptId }) => {
        const tabRemovedOrReset$ = tabRemovedOrReset(action$, tabId, state$);

        return streaming.smartQuoteMessage$.pipe(
          ofType('NextSmartQuoteResponse'),
          filter(res => res.id === id && res.promptId === promptId),
          map(nextSmartQuoteResponse => {
            // When spBridge send NextSmartQuoteResponse, it means a new quote is being requested so we dispatch the quoteRequested action to listen to the quote result
            return handleQuoteResponseThunk(nextSmartQuoteResponse, tabId);
          }),
          takeUntil(tabRemovedOrReset$),
        );
      }),
    );
}

export function parsingFinishedStreamingEpic(): EpicWithThunk {
  return (action$, state$, { streaming }) =>
    action$.pipe(
      ofType('PARSE_EMAIL_SUCCESS'),
      switchMap(({ tabId, promptId }) => {
        const tabRemovedOrReset$ = tabRemovedOrReset(action$, tabId, state$);
        return streaming.smartQuoteMessage$.pipe(
          ofType('SmartQuoteClosedEvent'),
          filter(res => res.promptId === promptId),
          map(smartQuoteClosedEvent => {
            return smartQuoteActions.parsingFinished(tabId, smartQuoteClosedEvent);
          }),
          takeUntil(tabRemovedOrReset$),
        );
      }),
    );
}

export function quoteResultEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { streaming, oldHttpClient, getNow }) =>
    action$.pipe(
      ofType('QUOTE_REQUESTED'),
      filter(action => action.workspace === 'smartQuote'),
      mergeMap(({ tabId, frontReconciliationId }) => {
        const quoteResponse$ = quoteResponseObservable(action$, frontReconciliationId);
        const quotePriceResponse$ = quotePriceResponse(streaming, oldHttpClient, getNow, frontReconciliationId, tabId);
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, quoteResponse$, tabId, state$);
        const quoteTimedOut$ = quoteTimedOut(quoteResponse$, getNow, tabId, appScheduler);

        return race(quotePriceResponse$, quoteTimedOut$).pipe(takeUntil(quoteOrTabRemoved$), take(1));
      }),
    );
}

function quoteResponseObservable(action$: Observable<Action>, frontReconciliationId: string) {
  return action$.pipe(
    ofType('QUOTE_RESPONSE'),
    filter(({ rfqId }) => rfqId === frontReconciliationId),
  );
}

function quotePriceResponse(
  streaming: Streaming,
  oldHttpClient: IHttpClient,
  getNow: () => Date,
  frontReconciliationId: string,
  tabId: string,
): Observable<Action | Thunk<void>> {
  return staticQuoteResult(streaming).pipe(
    filter(event => {
      return event.reconciliationId === frontReconciliationId;
    }),
    mergeMap(lightEvent =>
      fatEventApiConnector(oldHttpClient)
        .getFatEvent<QuoteResultDto<StaticFormResult>>(lightEvent)
        .pipe(
          map(quoteResult => handleQuotePriceResponseThunk(quoteResult, tabId)),
          catchError(() => {
            return of(
              commonQuoteAction.quoteResultFailed(
                'smartQuote',
                tabId,
                lightEvent.quoteId!,
                { message: 'quote.errorMessages.TechnicalError' },
                getNow(),
              ),
            );
          }),
        ),
    ),
  );
}

function quoteTimedOut(
  parsingResult$: Observable<QuoteResponse>,
  getNow: () => Date,
  tabId: string,
  appScheduler?: SchedulerLike,
): Observable<Action | Thunk<void>> {
  return parsingResult$.pipe(
    mergeMap(({ rfqId, pricingEstimatedDurationInMilliseconds }) => {
      return of(
        commonQuoteAction.quoteResultFailed(
          'smartQuote',
          tabId,
          rfqId,
          {
            message: 'quote.errorMessages.responseTimeout',
            values: { timeInMillis: pricingEstimatedDurationInMilliseconds },
          },
          getNow(),
        ),
      ).pipe(delay(pricingEstimatedDurationInMilliseconds as number, appScheduler));
    }),
  );
}

function quoteOrTabRemoved(
  action$: Observable<Action>,
  parsingResult$: Observable<QuoteResponse>,
  tabId: string,
  state$: StateObservable<AppState>,
): Observable<Action | Thunk<void>> {
  return merge(
    tabRemoved(action$, tabId, state$),
    parsingResult$.pipe(
      mergeMap(({ rfqId }) => {
        return buildQuoteOrTabRemoved(action$, tabId, rfqId, state$);
      }),
    ),
  ).pipe(take(1));
}

function tabRemovedOrReset(
  action$: Observable<Action>,
  tabId: string,
  state$: StateObservable<AppState>,
): Observable<Action> {
  return merge(
    tabRemoved(action$, tabId, state$),
    action$.pipe(
      ofType('WORKSPACE_RESET_TAB_REQUESTED'),
      filter(action => action.workspace === 'smartQuote' && action.tabId === tabId),
    ),
  ).pipe(take(1));
}
